import { useEffect, useState } from "react";
import { MapPin, Navigation, Send, Truck, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Field } from "@/components/common";
import { api, brl, errMsg, waLink, PAY_LABELS } from "@/lib/api";
import { orderStops, routeUrl, courierText, MAX_STOPS } from "@/lib/route";
import { toast } from "sonner";

const STATUS = { confirmado: "Confirmado", preparo: "Em preparação", pronto: "Pronto", entrega: "Saiu para entrega" };

function StopRow({ o, idx, checked, onToggle, notify }) {
  return (
    <li className="flex items-start gap-3 px-3 py-2 text-sm" data-testid={`route-order-${o.number}`}>
      <Checkbox checked={checked} onCheckedChange={onToggle} className="mt-0.5" data-testid={`route-check-${o.number}`} />
      <div className="flex-1 min-w-0">
        <p className="font-medium">{idx != null && <span className="mr-1 rounded bg-primary px-1.5 font-mono text-xs text-primary-foreground">{idx + 1}</span>}#{o.number} · {o.customer.name} <span className="text-xs text-muted-foreground">· {STATUS[o.status]}</span></p>
        <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className={`h-3 w-3 shrink-0 ${o.customer.lat != null ? "text-emerald-500" : ""}`} /><span className="truncate">{o.customer.address}</span></p>
      </div>
      <span className="font-mono text-xs">{brl(o.total)}</span>
      {o.status === "entrega" && o.customer.phone && <a href={notify(o)} target="_blank" rel="noreferrer" title="Avisar cliente" className="text-emerald-600" data-testid={`route-notify-${o.number}`}><MessageCircle className="h-4 w-4" /></a>}
    </li>
  );
}

export function RouteDialog({ open, onOpenChange, orders, couriers, store, storeName, notify, onDone }) {
  const candidates = orders.filter((o) => o.type === "delivery" && STATUS[o.status]);
  const [sel, setSel] = useState([]);
  const [courier, setCourier] = useState("");
  const [busy, setBusy] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) { setSel(candidates.filter((o) => o.status === "pronto").slice(0, MAX_STOPS).map((o) => o.id)); setCourier(""); } }, [open]);
  const stops = orderStops(store, candidates.filter((o) => sel.includes(o.id)));
  const tooMany = stops.length > MAX_STOPS;
  const url = stops.length && !tooMany ? routeUrl(store, stops) : "";
  const c = couriers.find((x) => x.id === courier);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const dispatch = async () => {
    setBusy(true);
    try {
      for (const o of stops) {
        if (courier && o.courier?.id !== courier) await api.patch(`/orders/${o.id}`, { courier_id: courier });
        if (o.status !== "entrega") await api.patch(`/orders/${o.id}/status`, { status: "entrega", reason: "" });
      }
      toast.success("Pedidos marcados como saiu para entrega");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const ordered = [...stops, ...candidates.filter((o) => !sel.includes(o.id))];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="route-modal">
        <DialogHeader><DialogTitle>Rota do entregador</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Marque os pedidos (até {MAX_STOPS}). A ordem das paradas é montada pela proximidade a partir da loja. Endereços sem localização (sem o pino verde) vão para o fim da rota.</p>
        <ul className="divide-y rounded-lg border" data-testid="route-orders-list">
          {ordered.map((o) => <StopRow key={o.id} o={o} idx={sel.includes(o.id) ? stops.indexOf(o) : null} checked={sel.includes(o.id)} onToggle={() => toggle(o.id)} notify={notify} />)}
          {!candidates.length && <li className="px-3 py-6 text-center text-sm text-muted-foreground">Nenhuma entrega em aberto.</li>}
        </ul>
        {tooMany && <p className="text-sm text-red-500" data-testid="route-too-many">Selecione no máximo {MAX_STOPS} pedidos por rota.</p>}
        <Field label="Entregador (opcional)">
          <select value={courier} onChange={(e) => setCourier(e.target.value)} className="h-10 rounded-md border bg-transparent px-2 text-sm" data-testid="route-courier-select">
            <option value="">Sem entregador definido</option>
            {couriers.filter((x) => x.active).map((x) => <option key={x.id} value={x.id}>{x.name}{x.phone ? ` · ${x.phone}` : ""}</option>)}
          </select>
        </Field>
        <p className="text-xs text-muted-foreground">No navegador do celular o Google Maps pode aceitar menos paradas; no aplicativo ou no computador aceita todas.</p>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" asChild disabled={!url}><a href={url || undefined} target="_blank" rel="noreferrer" aria-disabled={!url} className={!url ? "pointer-events-none opacity-50" : ""} data-testid="route-open-btn"><Navigation className="h-4 w-4 mr-1" />Abrir rota</a></Button>
          <Button variant="outline" asChild><a href={url && c?.phone ? waLink(c.phone, courierText(storeName, stops, url, PAY_LABELS)) : undefined} target="_blank" rel="noreferrer" className={!url || !c?.phone ? "pointer-events-none opacity-50" : ""} data-testid="route-send-courier-btn"><Send className="h-4 w-4 mr-1" />Enviar ao entregador</a></Button>
          <Button onClick={dispatch} disabled={!stops.length || tooMany || busy} data-testid="route-dispatch-btn"><Truck className="h-4 w-4 mr-1" />Confirmar saída</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
